<template>
  <div
    class="flex w-full flex-col rounded-md bg-default p-5"
    :class="{ 'gap-5': !readOnly }"
  >
    <h4 class="text-title-color2">Zuteilung Wärmeversorgungsgebiete</h4>
    <div class="flex w-[35%] flex-col gap-5">
      <InputEl
        :key="heatSupplyThresholds[0].key"
        v-model="dominantTechnologyVeryLikelyThreshold"
        :label="heatSupplyThresholds[0].label"
        :disabled="readOnly"
        suffix="%"
        input-type="float"
        :tooltip-text="heatSupplyThresholds[0].tooltip"
        :rules="{
          required: true,
          min: scenario[heatSupplyThresholds[1].key] || 0,
          max: 100,
        }"
        @update:model-value="emitHeatNetworkThresholds"
      />
      <InputEl
        :key="heatSupplyThresholds[1].key"
        v-model="dominantTechnologyLikelyThreshold"
        :label="heatSupplyThresholds[1].label"
        :disabled="readOnly"
        suffix="%"
        input-type="float"
        :tooltip-text="heatSupplyThresholds[1].tooltip"
        :rules="{
          required: true,
          min: scenario[heatSupplyThresholds[2].key] || 0,
          max: scenario[heatSupplyThresholds[0].key] || 100,
        }"
        @update:model-value="emitHeatNetworkThresholds"
      />
      <InputEl
        :key="heatSupplyThresholds[2].key"
        v-model="dominantTechnologyNotLikelyThreshold"
        :label="heatSupplyThresholds[2].label"
        :disabled="readOnly"
        suffix="%"
        input-type="float"
        :tooltip-text="heatSupplyThresholds[2].tooltip"
        :rules="{
          required: true,
          min: 0,
          max: scenario[heatSupplyThresholds[1].key] || 100,
        }"
        @update:model-value="emitHeatNetworkThresholds"
      />
    </div>
  </div>
  <ScenarioTabWrapper
    title="Manuelle Festlegung von Vorranggebieten"
    :enable-collapse="true"
    :read-only
    :available-items="availableItems"
    :filtered-items="districtsForPriorityAreas"
    :selected-items="selectedPriorityAreasDistricts"
    :headers="tableHeaders.priorityArea"
    :key-name-for-checkbox="'district'"
    :apply-selection-enabled="props.scenario.municipalities.asList.length > 0"
    :granular-selection-enabled="true"
    :show-unset-filter="false"
    @all-selection-button-click="
      emitPriorityData(districtParameterizations.map((d) => d.district))
    "
    @granular-selection-button-click="
      emitPriorityData(selectedPriorityAreasDistricts)
    "
    @update:filter="priorityAreasFilters = $event"
    @item-click="selectedPriorityAreasDistricts = [...$event]"
  >
    <DropDown
      v-if="!readOnly"
      v-model="priorityArea"
      :items-data="scenarioDistrictOptions.priority_area.choices"
      items-data-key="display_name"
      placeholder="Keine"
      label="Typ Vorranggebiet"
      :clearable="true"
      class="w-fit min-w-[400px]"
    />
  </ScenarioTabWrapper>
  <ScenarioTabWrapper
    title="Festlegung technologiespezifischer Ausbaulimits pro Jahr"
    title-tooltip="Ausbaulimit: maximaler Wert, bis zu dem die jeweilige Technologie ausgebaut wird"
    :enable-collapse="true"
    :read-only
    :available-items="availableItems"
    :filtered-items="districtsForExpansionLimit"
    :selected-items="selectedExpansionLimitDistricts"
    :headers="tableHeaders.expansionLimit"
    :key-name-for-checkbox="'district'"
    :apply-selection-enabled="props.scenario.municipalities.asList.length > 0"
    :granular-selection-enabled="true"
    :show-unset-filter="false"
    @all-selection-button-click="
      emitExpansionLimitData(districtParameterizations.map((d) => d.district))
    "
    @granular-selection-button-click="
      emitExpansionLimitData(selectedExpansionLimitDistricts)
    "
    @update:filter="expansionLimitFilters = $event"
    @item-click="selectedExpansionLimitDistricts = [...$event]"
  >
    <template v-if="!readOnly">
      <DropDown
        v-model="expansionLimitUnit"
        :initial-selection="true"
        :items-data="scenarioDistrictOptions.expansion_limit_unit.choices"
        items-data-key="display_name"
        class="w-fit min-w-[400px]"
        label="Einheit Ausbaulimit"
        :rules="{ required: true }"
      />
      <div
        v-for="choice in scenarioDistrictOptions.priority_area.choices"
        :key="choice.value"
        class="flex items-end gap-2.5"
      >
        <CheckboxEl
          v-model="selectedPriorityAreas[choice.value]"
          class="mb-3"
          @update:model-value="resetIfDeactivated(choice.value)"
        />
        <InputEl
          :model-value="
            expansionLimitValue[
              choice.value === 'DIS'
                ? 'expansion_limit_heating_network'
                : choice.value === 'H2'
                  ? 'expansion_limit_hydrogen_network'
                  : choice.value === 'PMP'
                    ? 'expansion_limit_decentral'
                    : null
            ]
          "
          :label="choice.display_name"
          :input-type="expansionLimitUnit === 2 ? 'integer' : 'float'"
          :placeholder="displayNameTechnicalUnit.placeholder"
          :suffix="displayNameTechnicalUnit.suffix"
          :disabled="!selectedPriorityAreas[choice.value]"
          class="w-fit min-w-[374px]"
          @update:model-value="
            expansionLimitValue[
              choice.value === 'DIS'
                ? 'expansion_limit_heating_network'
                : choice.value === 'H2'
                  ? 'expansion_limit_hydrogen_network'
                  : choice.value === 'PMP'
                    ? 'expansion_limit_decentral'
                    : null
            ] = $event
          "
        />
      </div>
    </template>
  </ScenarioTabWrapper>
</template>

<script setup>
import {
  heatSupplyThresholds,
  tableHeaders,
} from '@/apps/scenarios/scenario-create-data';
import { computed, ref, watch } from 'vue';
import DropDown from '@/components/storybook/src/stories/DropDown/DropDown.vue';
import DetailSelection from '@/apps/scenarios/components/shared/DetailSelection.vue';
import CheckboxEl from '@/components/storybook/src/stories/CheckboxEl/CheckboxEl.vue';
import InputEl from '@/components/storybook/src/stories/input/InputEl.vue';
import { isNullOrUndefined } from '@/utils/generalUtils';
import ToolTipOnHover from '@/components/storybook/src/stories/toolTipOnHover/ToolTipOnHover.vue';
import { useDetailFiltering } from '@/apps/scenarios/detailFiltering';
import ScenarioTabWrapper from '@/apps/scenarios/components/shared/ScenarioTabWrapper.vue';

const props = defineProps({
  scenario: Object,
  activeTab: Number,
  scenarioDistrictOptions: Object,
  districtParameterizations: Array,
  availableCounties: Array,
  availableItems: Object,
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['setOptionalParameter', 'setHeatNetworkThresholds']);

const {
  selectedItems: selectedPriorityAreasDistricts,
  filters: priorityAreasFilters,
  filteredItems: filteredPriorityAreasDistricts,
} = useDetailFiltering(props.districtParameterizations, props.availableItems, [
  'priority_area',
]);
const {
  selectedItems: selectedExpansionLimitDistricts,
  filters: expansionLimitFilters,
  filteredItems: filteredExpansionLimitDistricts,
} = useDetailFiltering(props.districtParameterizations, props.availableItems, [
  'expansion_limit_unit',
  'expansion_limit_heating_network',
  'expansion_limit_hydrogen_network',
  'expansion_limit_decentral',
]);

// setting Values Logic
const priorityArea = ref(null);
const expansionLimitUnit = ref(null);
const expansionLimitValue = ref({
  expansion_limit_heating_network: null,
  expansion_limit_hydrogen_network: null,
  expansion_limit_decentral: null,
});
const selectedPriorityAreas = ref({});
const dominantTechnologyVeryLikelyThreshold = ref(
  props.scenario.dominantTechnologyVeryLikelyThreshold,
);
const dominantTechnologyLikelyThreshold = ref(
  props.scenario.dominantTechnologyLikelyThreshold,
);
const dominantTechnologyNotLikelyThreshold = ref(
  props.scenario.dominantTechnologyNotLikelyThreshold,
);

function emitPriorityData(districtIds) {
  const updateDistrictParameter = {};
  for (const districtId of districtIds) {
    updateDistrictParameter[districtId] = {
      priority_area: priorityArea.value,
    };
  }
  emit('setOptionalParameter', updateDistrictParameter);
}

function emitHeatNetworkThresholds() {
  emit('setHeatNetworkThresholds', {
    dominantTechnologyVeryLikelyThreshold:
      dominantTechnologyVeryLikelyThreshold.value,
    dominantTechnologyLikelyThreshold: dominantTechnologyLikelyThreshold.value,
    dominantTechnologyNotLikelyThreshold:
      dominantTechnologyNotLikelyThreshold.value,
  });
}

function emitExpansionLimitData(districtIds) {
  const updateDistrictParameter = {};
  for (const districtId of districtIds) {
    updateDistrictParameter[districtId] = {
      expansion_limit_unit: expansionLimitUnit.value,
      ...expansionLimitValue.value,
    };
  }
  emit('setOptionalParameter', updateDistrictParameter);
}

const expansionLimitMap = {
  DIS: 'expansion_limit_heating_network',
  H2: 'expansion_limit_hydrogen_network',
  PMP: 'expansion_limit_decentral',
};

function resetIfDeactivated(choice) {
  const expansionKey = expansionLimitMap[choice];
  if (selectedPriorityAreas.value[choice] === false) {
    expansionLimitValue.value[expansionKey] = null;
  }
}

const districtsForPriorityAreas = computed(() => {
  return filteredPriorityAreasDistricts.value.map((district) => {
    const priorityAreaChoice =
      props.scenarioDistrictOptions.priority_area.choices.find(
        (item) => item.value === district.priority_area,
      );

    return {
      ...district,
      priorityArea: priorityAreaChoice?.display_name || '-',
    };
  });
});

const districtsForExpansionLimit = computed(() => {
  return filteredExpansionLimitDistricts.value.map((district) => {
    const {
      expansion_limit_unit,
      expansion_limit_heating_network,
      expansion_limit_hydrogen_network,
      expansion_limit_decentral,
    } = district;
    const unitChoice =
      props.scenarioDistrictOptions.expansion_limit_unit.choices.find(
        (item) => item.value === expansion_limit_unit,
      );
    const unitDisplayName =
      unitChoice?.display_name === 'Hausanschlüsse in Anzahl'
        ? 'Hausanschlüsse'
        : unitChoice?.display_name.split(' ').pop();
    const heatingNetworkVal = isNullOrUndefined(expansion_limit_heating_network)
      ? '-'
      : `${expansion_limit_heating_network} ${unitDisplayName}`;
    const hydroNetworkVal = isNullOrUndefined(expansion_limit_hydrogen_network)
      ? '-'
      : `${expansion_limit_hydrogen_network} ${unitDisplayName}`;
    const decentralAreaVal = isNullOrUndefined(expansion_limit_decentral)
      ? '-'
      : `${expansion_limit_decentral} ${unitDisplayName}`;

    return {
      ...district,
      heatingNetwork: heatingNetworkVal,
      hydrogenNetwork: hydroNetworkVal,
      decentralArea: decentralAreaVal,
    };
  });
});

const displayNameTechnicalUnit = computed(() => {
  const selectedUnitOption =
    props.scenarioDistrictOptions.expansion_limit_unit.choices.find(
      (unit) => unit.value === expansionLimitUnit.value,
    );
  const displayName = selectedUnitOption ? selectedUnitOption.display_name : '';
  const placeholder = `${displayName.split(' ').shift()} eingeben`;
  const suffix =
    displayName === 'Hausanschlüsse' ? 'Anzahl' : displayName.split(' ').pop();
  return { placeholder, suffix };
});

watch(props.scenarioDistrictOptions, () => {
  props.scenarioDistrictOptions.priority_area.choices.forEach((choice) => {
    selectedPriorityAreas.value[choice.value] = false;
  });
});
</script>

<style scoped></style>
